'use client'

import { useEffect, useRef, useState, useTransition } from 'react'

import { authClient } from '@/auth-client'
import type { EnabledProviders } from '@/lib/auth/enabled-providers'

type ErrorCode = 'expired' | 'consumed' | 'invalid' | 'unknown'

const ERROR_COPY: Record<ErrorCode, string> = {
    expired: 'This sign-in link has expired.\nSend a new one below.',
    consumed:
        'This sign-in link has already been used.\nSend a new one if you need to sign in again.',
    invalid: "This sign-in link isn't valid.\nSend a new one below.",
    unknown: 'Couldn’t sign you in. Send a new link below.',
}

function normaliseErrorCode(raw: string | null): ErrorCode | null {
    if (!raw) return null
    switch (raw.toLowerCase()) {
        case 'expired':
        case 'token-expired':
            return 'expired'
        case 'consumed':
        case 'already-used':
        case 'token-already-used':
            return 'consumed'
        case 'invalid':
        case 'token-invalid':
            return 'invalid'
        default:
            return 'unknown'
    }
}

/**
 * Signin auth-form client island. Holds every piece of interactive state
 * for the signin page: OAuth button handlers, magic-link form expansion,
 * submit state, after-submit body swap, after-error state, resend cooldown.
 *
 * Single client island over per-button micro-islands — over-segments the
 * shared state (typed email, cooldown timer, expanded flag, submitted flag).
 *
 * Spec: docs/superpowers/specs/2026-05-13-signin-mini-spec.md §1.1 (row 3-4),
 * §1.4 (after-submit state), §1.5 (after-error state), §4.4 (server-client
 * split), §4.6 (client state machine).
 */
export function AuthForm({
    enabledProviders,
    initialFormExpanded,
    errorCode: rawErrorCode,
    initialEmail,
}: {
    enabledProviders: EnabledProviders
    initialFormExpanded: boolean
    errorCode: string | null
    initialEmail: string
}) {
    const errorCode = normaliseErrorCode(rawErrorCode)
    const [expanded, setExpanded] = useState(
        initialFormExpanded || errorCode !== null
    )
    const [email, setEmail] = useState(initialEmail)
    const [pending, startTransition] = useTransition()
    const [submitted, setSubmitted] = useState(false)
    const [submittedEmail, setSubmittedEmail] = useState('')
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [cooldown, setCooldown] = useState(0)
    const [oauthPending, setOauthPending] = useState<
        'google' | 'discord' | null
    >(null)
    const [oauthError, setOauthError] = useState<string | null>(null)
    const [resendError, setResendError] = useState<string | null>(null)
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

    useEffect(() => {
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current)
        }
    }, [])

    const startCooldown = () => {
        setCooldown(60)
        if (intervalRef.current) clearInterval(intervalRef.current)
        intervalRef.current = setInterval(() => {
            setCooldown((n) => {
                if (n <= 1) {
                    if (intervalRef.current) clearInterval(intervalRef.current)
                    return 0
                }
                return n - 1
            })
        }, 1000)
    }

    const handleOAuth = (provider: 'google' | 'discord') => {
        setOauthError(null)
        setOauthPending(provider)
        authClient.signIn
            .social({ provider, callbackURL: '/journey' })
            .then(() => {
                if (typeof window !== 'undefined') {
                    localStorage.setItem('hasAlreadySignedIn', 'true')
                }
            })
            .catch((error: unknown) => {
                const message =
                    error instanceof Error ? error.message : 'Sign-in failed'
                setOauthError(message)
                setOauthPending(null)
            })
    }

    const handleResend = () => {
        if (cooldown > 0 || pending) return
        setResendError(null)
        startTransition(async () => {
            const result = await authClient.signIn.magicLink({
                email: submittedEmail,
                callbackURL: '/journey',
            })
            if (result.error) {
                setResendError(
                    result.error.message ??
                        'Couldn’t send another link. Try again.'
                )
                return
            }
            startCooldown()
        })
    }

    const handleUseDifferent = () => {
        setSubmitted(false)
        setSubmittedEmail('')
        setEmail('')
        setResendError(null)
        if (intervalRef.current) clearInterval(intervalRef.current)
        setCooldown(0)
    }

    if (submitted) {
        return (
            <div className="flex flex-col items-center text-center">
                <h2 className="font-serif text-h2 tracking-tight text-ink">
                    A sign-in link is on its way.
                </h2>
                <p className="mt-3 font-serif text-base text-muted-fg italic">
                    Check inbox for {submittedEmail} — or its spam folder.
                    <br />
                    The link expires in 10 minutes.
                </p>
                <div className="mt-8 ml-auto flex flex-col items-end gap-2">
                    <button
                        type="button"
                        onClick={handleResend}
                        disabled={cooldown > 0 || pending}
                        // reason: text-link affordance hover transitions to
                        // text-action (chrome-strip pattern); disabled state
                        // skips the hover transition. Vermilion only on hover,
                        // not at rest — preserves §6 budget.
                        className="inline-flex items-center gap-1 font-serif text-base tracking-wide text-muted-fg italic transition-colors hover:text-action focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-focus-ring disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none"
                    >
                        {pending
                            ? 'Sending…'
                            : cooldown > 0
                              ? `Send another in ${cooldown}s…`
                              : 'Send another →'}
                    </button>
                    {resendError && (
                        <p
                            role="alert"
                            className="font-serif text-sm text-error"
                        >
                            {resendError}
                        </p>
                    )}
                    <button
                        type="button"
                        onClick={handleUseDifferent}
                        // reason: secondary recovery affordance — same hover
                        // transition family, not at-rest vermilion.
                        className="font-serif text-base tracking-wide text-muted-fg italic transition-colors hover:text-action focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-focus-ring motion-reduce:transition-none"
                    >
                        Use a different email
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-3">
            {(oauthError ?? errorCode) && (
                <p
                    role="alert"
                    className="text-center font-serif text-base whitespace-pre-line text-body-fg italic"
                >
                    {oauthError ?? ERROR_COPY[errorCode!]}
                </p>
            )}

            {enabledProviders.google && (
                <button
                    type="button"
                    onClick={() => handleOAuth('google')}
                    disabled={oauthPending !== null}
                    className="flex w-full items-center justify-center border border-rule bg-paper px-6 py-4 font-serif text-base text-ink transition-colors hover:border-ink hover:bg-surface-elevated focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none"
                >
                    {oauthPending === 'google'
                        ? 'Signing in…'
                        : 'Continue with Google'}
                </button>
            )}

            {enabledProviders.discord && (
                <button
                    type="button"
                    onClick={() => handleOAuth('discord')}
                    disabled={oauthPending !== null}
                    className="flex w-full items-center justify-center border border-rule bg-paper px-6 py-4 font-serif text-base text-ink transition-colors hover:border-ink hover:bg-surface-elevated focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none"
                >
                    {oauthPending === 'discord'
                        ? 'Signing in…'
                        : 'Continue with Discord'}
                </button>
            )}

            {enabledProviders.email && (
                <>
                    <div aria-hidden className="my-2 flex items-center gap-3">
                        <span className="h-px flex-1 bg-rule" />
                        <span className="font-serif text-sm tracking-wide text-muted-fg italic">
                            or
                        </span>
                        <span className="h-px flex-1 bg-rule" />
                    </div>

                    {expanded ? (
                        <form
                            onSubmit={(e) => {
                                e.preventDefault()
                                const trimmed = email.trim()
                                if (trimmed.length === 0) return

                                setSubmitError(null)
                                startTransition(async () => {
                                    const result =
                                        await authClient.signIn.magicLink({
                                            email: trimmed,
                                            callbackURL: '/journey',
                                        })
                                    if (result.error) {
                                        setSubmitError(
                                            result.error.message ??
                                                'Couldn’t send the link. Try again.'
                                        )
                                        return
                                    }
                                    setSubmittedEmail(trimmed)
                                    setSubmitted(true)
                                    startCooldown()
                                })
                            }}
                            className="flex flex-col gap-3"
                        >
                            <label
                                htmlFor="magic-link-email"
                                className="sr-only"
                            >
                                Your email address
                            </label>
                            <input
                                id="magic-link-email"
                                type="email"
                                autoComplete="email"
                                autoFocus
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                disabled={pending}
                                className="w-full border border-rule bg-surface-sunken px-4 py-3 font-serif text-base text-ink placeholder:text-muted-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring disabled:opacity-60"
                            />
                            {submitError && (
                                <p
                                    role="alert"
                                    className="font-serif text-sm text-error"
                                >
                                    {submitError}
                                </p>
                            )}
                            <button
                                type="submit"
                                disabled={pending || email.trim().length === 0}
                                className="inline-flex w-full items-center justify-center gap-2 border border-rule bg-paper px-6 py-3 font-serif text-base text-ink transition-colors hover:border-ink hover:bg-surface-elevated focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none"
                            >
                                {pending ? 'Sending…' : 'Send sign-in link →'}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setExpanded(false)
                                    setEmail('')
                                    setSubmitError(null)
                                }}
                                disabled={pending}
                                // reason: cancel affordance mirrors the magic-link
                                // text-link below — text-action only on hover, not
                                // at-rest. Vermilion stays scarce per spine §7.4.
                                className="font-serif text-sm text-muted-fg italic transition-colors hover:text-action focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-focus-ring disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none"
                            >
                                cancel
                            </button>
                        </form>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setExpanded(true)}
                            // reason: magic-link affordance text-link — hover
                            // transitions to text-action (chrome-strip pattern).
                            // Vermilion only on hover, not at-rest. Spec §6.
                            className="inline-flex items-center gap-1 self-center py-3 font-serif text-base tracking-wide text-muted-fg italic transition-colors hover:text-action focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-focus-ring motion-reduce:transition-none"
                        >
                            or sign in with email →
                        </button>
                    )}
                </>
            )}
        </div>
    )
}
